import React, { useState, useEffect } from 'react';
import { adminStatsService } from '../../../services/adminStatsService';
import { useSeasonContext } from '../../../context/SeasonContext';

const PlayerComparisonBlock = ({ block, onUpdate }) => {
  const [player1Name, setPlayer1Name] = useState(block.player1_name || '');
  const [player2Name, setPlayer2Name] = useState(block.player2_name || '');
  const [player1Team, setPlayer1Team] = useState(block.player1_team || '');
  const [player2Team, setPlayer2Team] = useState(block.player2_team || '');
  const [player1Stats, setPlayer1Stats] = useState(block.player1_stats || {});
  const [player2Stats, setPlayer2Stats] = useState(block.player2_stats || {});
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('PASSING');
  const { selectedSeason } = useSeasonContext();

  const statCategories = ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'BLOCKING', 'KICKING'];

  useEffect(() => {
    if (selectedSeason?.id) {
      fetchPlayers();
    }
  }, [selectedSeason?.id]);

  const fetchPlayers = async () => {
    setLoading(true);
    const playerList = await adminStatsService.getPlayersForSeason(selectedSeason.id);
    setPlayers(playerList);
    setLoading(false);
  };

  const fetchPlayerStats = async (playerName) => {
    if (!playerName || !selectedSeason?.id) return {};
    
    const stats = {};
    for (const category of statCategories) {
      const result = await adminStatsService.getAllSeasonStats(category, selectedSeason.id);
      const playerData = result.find(p => p.NAME === playerName);
      if (playerData) {
        stats[category] = playerData;
      }
    }
    return stats;
  };

  const handlePlayer1Change = async (e) => {
    const selectedName = e.target.value;
    const selectedPlayer = players.find(p => p.name === selectedName);
    setPlayer1Name(selectedName);
    setPlayer1Team(selectedPlayer?.team || '');
    
    const stats = await fetchPlayerStats(selectedName);
    setPlayer1Stats(stats);
    
    onUpdate(block.id, { 
      player1_name: selectedName,
      player1_team: selectedPlayer?.team || '',
      player1_stats: stats
    });
  };

  const handlePlayer2Change = async (e) => {
    const selectedName = e.target.value;
    const selectedPlayer = players.find(p => p.name === selectedName);
    setPlayer2Name(selectedName);
    setPlayer2Team(selectedPlayer?.team || '');
    
    const stats = await fetchPlayerStats(selectedName);
    setPlayer2Stats(stats);
    
    onUpdate(block.id, { 
      player2_name: selectedName,
      player2_team: selectedPlayer?.team || '',
      player2_stats: stats
    });
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return value.toFixed(2);
    return value;
  };

  const compareValues = (v1, v2) => {
    if (!v1 || !v2) return 0;
    const n1 = parseFloat(v1);
    const n2 = parseFloat(v2);
    if (n1 > n2) return 1;
    if (n1 < n2) return -1;
    return 0;
  };

  const getStatKeys = () => {
    const p1Data = player1Stats[activeCategory];
    const p2Data = player2Stats[activeCategory];
    if (p1Data) return Object.keys(p1Data).filter(k => k !== 'NAME' && k !== 'TEAM' && k !== 'ID' && k !== 'POS');
    if (p2Data) return Object.keys(p2Data).filter(k => k !== 'NAME' && k !== 'TEAM' && k !== 'ID' && k !== 'POS');
    return [];
  };

  const p1Data = player1Stats[activeCategory];
  const p2Data = player2Stats[activeCategory];
  const statKeys = getStatKeys();

  return (
    <div className="player-comparison-block">
      <div className="comparison-form">
        <div className="form-group">
          <label>Player 1</label>
          <select
            value={player1Name}
            onChange={handlePlayer1Change}
            disabled={loading || players.length === 0}
            className="player-select"
          >
            <option value="">Select player</option>
            {players.map((player) => (
              <option key={player.name} value={player.name}>
                {player.name} {player.team && `(${player.team})`}
              </option>
            ))}
          </select>
          {player1Team && <p className="player-team">{player1Team}</p>}
        </div>
        <div className="form-group">
          <label>Player 2</label>
          <select
            value={player2Name}
            onChange={handlePlayer2Change}
            disabled={loading || players.length === 0}
            className="player-select"
          >
            <option value="">Select player</option>
            {players.map((player) => (
              <option key={player.name} value={player.name}>
                {player.name} {player.team && `(${player.team})`}
              </option>
            ))}
          </select>
          {player2Team && <p className="player-team">{player2Team}</p>}
        </div>
      </div>
      {player1Name && player2Name && (
        <div className="comparison-preview">
          <p className="preview-label">Preview:</p>
          <div className="comparison-header">
            <div className="comparison-player">
              <h4>{player1Name}</h4>
              <p className="player-team">{player1Team}</p>
            </div>
            <div className="comparison-vs">VS</div>
            <div className="comparison-player">
              <h4>{player2Name}</h4>
              <p className="player-team">{player2Team}</p>
            </div>
          </div>

          {(p1Data || p2Data) && (
            <>
              <div className="comparison-category-tabs">
                {statCategories.map(category => (
                  <button
                    key={category}
                    className={`category-tab ${activeCategory === category ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {statKeys.length > 0 && (
                <div className="comparison-stats-table">
                  <table>
                    <thead>
                      <tr>
                        <th className="stat-name">Statistic</th>
                        <th className="stat-value">{player1Name}</th>
                        <th className="stat-value">{player2Name}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statKeys.map((key) => {
                        const v1 = p1Data?.[key];
                        const v2 = p2Data?.[key];
                        const comparison = compareValues(v1, v2);
                        
                        return (
                          <tr key={key}>
                            <td className="stat-name">{key.replace(/_/g, ' ')}</td>
                            <td className={`stat-value ${comparison > 0 ? 'winner' : ''}`}>
                              {formatValue(v1)}
                            </td>
                            <td className={`stat-value ${comparison < 0 ? 'winner' : ''}`}>
                              {formatValue(v2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerComparisonBlock;
